import { FC } from 'react';

import { Box, Button, Modal, useMediaQuery, useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';

import {
  MacroNutrients,
  Tag,
} from '../../ts/@types/generated/graphql/catalog-service/graphql';
import AddRemoveMeal from '../AddRemoveMeal';
import Close from '../Icons/Close/Close';
import MealDetailsContent from './MealDetailsContent';
import MealDetailsHeader from './MealDetailsHeader';

interface Props {
  title: string;
  subtitle?: string;
  description: string;
  designer: string;
  macroNutrients: MacroNutrients;
  servingsTag?: Tag | null;
  nutritionCaveat?: string | null;
  publicIngredientsCopy: string;
  containsAllergensCopy: string | null;
  imageUrl: string;
  dietaryTemplateDisplayNames: string[];
  isNaeProteinSourcingStandard: boolean;
  shelfLifeTagId: number | undefined;
  kosherTagDisplayName?: string | null;
  isOpen: boolean;
  close: () => void;
  editCartFunctions?: {
    addMealToCart: (variantId: number) => void;
    removeMealFromCart: (variantId: number) => void;
  };
  quantityInCart?: number;
  variantId?: number;
  soldOut?: boolean;
}

const MealDetailsModal: FC<Props> = ({
  containsAllergensCopy,
  designer,
  description,
  dietaryTemplateDisplayNames,
  macroNutrients,
  servingsTag,
  publicIngredientsCopy,
  nutritionCaveat,
  title,
  subtitle,
  imageUrl,
  kosherTagDisplayName,
  isOpen,
  close,
  isNaeProteinSourcingStandard,
  shelfLifeTagId,
  editCartFunctions,
  quantityInCart,
  variantId,
  soldOut,
}) => {
  const theme = useTheme();

  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('sm'),
  );

  const hasCartControls =
    editCartFunctions && quantityInCart !== undefined && variantId;

  return (
    <Modal
      open={isOpen}
      onClose={close}
      style={{
        overflowY: 'auto',
        margin: isSmallScreen ? undefined : '2.75rem 0 0',
      }}
    >
      <Box
        sx={{
          width: isSmallScreen ? undefined : '900px',
          margin: isSmallScreen ? undefined : '0 auto 2.75rem',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '2px',
          '&:focus-visible': {
            outline: 'none',
          },
          position: 'relative',
        }}
      >
        <MealDetailsHeader
          title={title}
          subtitle={subtitle}
          designer={designer}
          action={
            <>
              {!isSmallScreen && hasCartControls ? (
                <AddRemoveMeal
                  editCartFunctions={editCartFunctions}
                  quantityInCart={quantityInCart}
                  variantId={variantId}
                  soldOut={!!soldOut}
                  showRemoveWithZero={true}
                  fontSize={'1.5rem'}
                  buttonSize={'2.2rem'}
                />
              ) : null}
              <Box
                onClick={close}
                sx={{
                  cursor: 'pointer',
                }}
              >
                <Close />
              </Box>
            </>
          }
        />

        {isSmallScreen && hasCartControls ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              padding: '0.75rem 1rem',
              borderBottom: '1px solid #DAD4BC',
            }}
          >
            <AddRemoveMeal
              editCartFunctions={editCartFunctions}
              quantityInCart={quantityInCart}
              variantId={variantId}
              soldOut={!!soldOut}
              showRemoveWithZero={true}
              fontSize={'1.5rem'}
              buttonSize={'3.5rem'}
            />
          </Box>
        ) : null}

        <MealDetailsContent
          title={title}
          subtitle={subtitle}
          description={description}
          macroNutrients={macroNutrients}
          servingsTag={servingsTag}
          nutritionCaveat={nutritionCaveat}
          publicIngredientsCopy={publicIngredientsCopy}
          containsAllergensCopy={containsAllergensCopy}
          imageUrl={imageUrl}
          dietaryTemplateDisplayNames={dietaryTemplateDisplayNames}
          isNaeProteinSourcingStandard={isNaeProteinSourcingStandard}
          shelfLifeTagId={shelfLifeTagId}
          kosherTagDisplayName={kosherTagDisplayName}
        />

        <Box sx={{ padding: '0.75rem 0.75rem' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant={'contained'}
              sx={{ width: '18.75rem', textTransform: 'none' }}
              onClick={close}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default MealDetailsModal;
